/**
 * The Turnstile (place manager, unit 9): the words behind a place's numbers.
 * Pure, and it reads nothing: the section draws from the room-now read and the
 * last-events read the page already makes. Its right-now numbers
 * (`rightNowOf`) stay in `place-today.tsx`, beside the room model they are
 * built from; moved here, the two modules would import each other.
 *
 * What the backend counts today: who is in each room now (live), and each
 * event's visitors (the people across its rooms, written when it ends). A
 * room's own count (`Gathering.totalVisitorCount`) is written only when the
 * room closes, from visit records deleted 24 hours after each person's last
 * visit, and cleared when a main room reopens; a place lists only its open
 * tables. So no all-time visitor count exists to show, and nothing over time.
 */
import type { PlaceData } from "@/data/places-api";
import { EVENTS_SHOWN, eventRowsOf, type EventRow, type PlaceEvents } from "@/features/places/last-events";
import type { Freshness } from "@/features/places/place-today";
import type { Load } from "@/features/places/use-places";
import { plural } from "@/lib/utils";

/** What the Turnstile cannot count yet, one line each: nothing over time is
 *  kept, and minutes together is measured by nothing today. */
export const TURNSTILE_LATER = [
  "Busiest hour and day",
  "How long people stay",
  "Attendance per event over time",
  "Minutes together",
] as const;

/** Where the live numbers come from, in a word: "live" after a good read;
 *  "last read" when the latest re-read failed (the last good numbers show);
 *  null with no read behind them (samples), so no word is said. */
export function sourceWord(freshness?: Freshness): string | null {
  if (!freshness) return null;
  return freshness.refreshFailed ? "last read" : "live";
}

/** `text`, with its source word after a "·" when there is one. */
export function withSource(text: string, freshness?: Freshness): string {
  const word = sourceWord(freshness);
  return word ? `${text} · ${word}` : text;
}

/** The line under the Turnstile: where its numbers come from. */
export function turnstileFooter(freshness?: Freshness): string {
  const now = !freshness
    ? "Here now, tables open and what is playing come from the room now."
    : freshness.refreshFailed
      ? "Here now, tables open and what is playing are the room now's last good read; the latest refresh failed."
      : "Here now, tables open and what is playing are live, from the room now.";
  return `${now} Who came is counted per event, when it ends. Nothing over time is kept yet.`;
}

/** What the Turnstile says about visitors, all time: not kept, and why, by
 *  what the place has (tables open, none, or a main room still being set up).
 *  An open room reads no count, never 0. A main room closed right now (it
 *  reopens within about a minute) shows what it counted. */
export function allTimeLine(place: PlaceData): string {
  const n = place.rootGathering?.totalVisitorCount;
  if (n === null || n === undefined) {
    const why = !place.rootGathering
      ? "The main room is still being set up."
      : place.subGatherings.totalCount > 0
        ? "The main room stays open, and the tables here have not closed, so none of them has a count."
        : "The main room stays open, so it has no count.";
    return `Not kept yet. A room's visitors are counted only when it closes, and only people from its last 24 hours. ${why}`;
  }
  return `The main room counted ${n.toLocaleString()} ${plural(n, "visitor")} when it last closed: the people there in its last 24 hours. A place-wide count is not kept yet.`;
}

/** Counts as words, up to {@link EVENTS_SHOWN} (a test ties the two). */
export const NUMBER_WORDS = ["zero", "one", "two", "three", "four", "five"];

/** The events group's heading: "Last five events", or as many as have begun;
 *  "Last events" (no count) while there are no rows to show. */
export function lastEventsTitle(events: Load<PlaceEvents | null>): string {
  const read = eventRowsOf(events);
  if (!read) return "Last events";
  const n = Math.min(read.rows.length, EVENTS_SHOWN);
  return n === 1 ? "The last event" : `Last ${NUMBER_WORDS[n] ?? n} events`;
}

/** Why an event's "— came" is a dash, so nobody-came, not-counted and failed
 *  never look alike: its results did not read; it is on now or just ended
 *  (the backend counts who came once it is closed out); or it finished
 *  uncounted. Null when the count is there. */
export function cameNote(row: EventRow): string | null {
  if (row.unread) return "results did not read";
  if (row.came !== null) return null;
  if (row.status === "on now") return "who came is counted when it ends";
  if (row.status !== null) return "who came is counted once it is closed out";
  return "who came was not counted";
}
